import React from 'react';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { keepFieldFocusOnPress } from './keepFieldFocusOnPress';
import type { QuizChromeKit } from './quizChromeKitDoor';
import type { QuizBackKind } from './quizHeader';

/**
 * Слот кнопки «Назад» в шапке плеера квиза: сама кнопка, а на первом экране —
 * невидимая распорка того же размера; у пака без кнопки — ничего (`null`).
 * Слот один на обе ветки шапки (обычная и со скрытым хромом, WEB-2092).
 *
 * Функция отрисовки, а не компонент: плеер кладёт её результат в шапку как
 * раньше клал ту же разметку — дерево узлов не меняется.
 *
 * WEB-3402: вынесено из QuizScreenWizard.tsx (шапка `chromeNode`) без
 * изменений поведения. Параметры позиционные, а не объектом: имена ключей
 * объекта минификатор не сжимает, а запас веса первого экрана покупателя —
 * десятки байт.
 *
 * @param backKind форма кнопки из пака (`chrome.backButton`; без пака — 'icon-bare')
 *   или своя форма поля шапки `circle-solid` (белый круг со стрелкой без текста,
 *   слово владельца 20.10.2026; круг и стрелка — из куска рамки)
 *   или `arrow` (голая стрелка «←» без круга и подписи, слово владельца 21.10.2026)
 * @param iconColor цвет значка (поле шапки buttonIconColor) — у `circle-solid`,
 *   `chevron` и `arrow`; нет — прежний цвет формы
 * @param isFirst истории переходов нет — возвращаться некуда
 */
export function renderQuizBackSlot(
  backKind: QuizBackKind,
  isFirst: boolean,
  onBack: () => void,
  language: string | undefined,
  chromeKit: QuizChromeKit,
  iconColor?: string,
): React.ReactElement | null {
  /**
   * Бокс кнопки «Назад» — одним объявлением: его же занимает НЕВИДИМАЯ
   * распорка на первом экране (см. распорку ниже). Две копии стилей
   * разъехались бы, и распорка держала бы не ту высоту.
   */
  const backButtonBoxStyle: React.CSSProperties = {
    background:
      backKind === 'pill' || backKind === 'icon-circle'
        ? 'var(--color-bg-surface, transparent)'
        : 'transparent',
    border:
      backKind === 'icon-circle'
        ? '1px solid var(--color-border-primary, #f0f0f0)'
        : 'none',
    borderRadius: backKind === 'icon-bare' ? 0 : 999,
    padding: backKind === 'icon-bare' ? 0 : '6px 12px',
    color: 'var(--color-primary, #1677ff)',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: backKind === 'pill' ? 600 : 400,
  };

  // Иконочные формы: подписи нет, только для читалки; бокс — из куска рамки.
  const iconOnly =
    backKind === 'chevron' || backKind === 'circle-solid' || backKind === 'arrow';
  // Голая стрелка: область нажатия шеврона, стрелка прижата к краю экрана;
  // без поля цвета — цвет текста шапки: у кнопки свой цвет браузера, а не цвет
  // страницы, а `color: currentColor` по CSS Color 4 значит «как у родителя»
  // (inherit). Как у Dance-bit — тёмная на светлом.
  const iconBoxStyle =
    backKind === 'circle-solid'
      ? { ...chromeKit.CIRCLE_SOLID_STYLE, ...(iconColor ? { color: iconColor } : {}) }
      : backKind === 'arrow'
        ? {
            ...chromeKit.chevronSlotStyle('back'),
            justifyContent: 'flex-start',
            paddingRight: 0,
            color: iconColor ?? 'currentColor',
          }
        : chromeKit.chevronSlotStyle('back');

  const backButton =
    !isFirst && backKind !== 'none' ? (
      <button
        onClick={onBack}
        // Ревизия пачки 8: без этого «Назад» уводил ВПЕРЁД — потеря фокуса
        // полем опережала клик и включала авто-переход (keepFieldFocusOnPress).
        onMouseDown={keepFieldFocusOnPress}
        data-testid="quiz-back-button"
        data-shape={backKind}
        // Пачка настроек 2: 'chevron' — только иконка, подпись для читалки.
        aria-label={iconOnly ? getPublicQuizStrings(language).backLabel : undefined}
        style={iconOnly ? iconBoxStyle : backButtonBoxStyle}
      >
        {backKind === 'circle-solid' || backKind === 'arrow' ? (
          <chromeKit.ArrowBackIcon />
        ) : backKind === 'chevron' ? (
          <span style={chromeKit.CHEVRON_BOX_STYLE}>
            <chromeKit.ChevronIcon direction="back" color={iconColor} />
          </span>
        ) : (
          <>← {getPublicQuizStrings(language).backLabel}</>
        )}
      </button>
    ) : null;

  /**
   * РЕВИЗИЯ ПАЧКИ 38 (попутный пункт): место кнопки держится в ОБЕИХ ветках
   * шапки. Раньше распорка стояла только в ветке обычной шапки, и притом
   * пустым `<span />` — в ветке со скрытым хромом строка на первом экране
   * схлопывалась в нулевую высоту, а содержимое экрана прыгало вверх-вниз
   * при первом же переходе. Распорка — невидимая копия бокса кнопки: тот же
   * кегль и те же поля, значит та же высота. `aria-hidden` и отсутствие
   * текста для читалки: для человека со скринридером её нет.
   *
   * СВЕЖИЙ ВЗГЛЯД ПАЧКИ 38 (Н3) — распорка ставится, только когда кнопка
   * БЫВАЕТ. У пака с `chrome.backButton: 'none'` (cinematic-photo,
   * editorial-serif) кнопки нет ни на одном экране: появляться нечему, а
   * невидимый бокс всё равно занимал высоту на КАЖДОМ шаге и опускал
   * содержимое. Распорка держит место, а не рисует пустоту там, где места
   * не нужно.
   */
  return backButton ?? (backKind === 'none' ? null : (
    <span
      aria-hidden="true"
      data-testid="quiz-back-spacer"
      style={{
        ...(iconOnly ? iconBoxStyle : backButtonBoxStyle),
        display: iconOnly ? 'flex' : 'inline-block',
        visibility: 'hidden',
        cursor: 'default',
      }}
    >
      {iconOnly ? null : <>← {getPublicQuizStrings(language).backLabel}</>}
    </span>
  ));
}
